import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
    MaterialReactTable,
    MRT_GlobalFilterTextField,
    MRT_ToggleFiltersButton,
    MRT_ToolbarAlertBanner
} from 'material-react-table';
import {MRT_Localization_RU} from 'material-react-table/locales/ru';
import {Dialog, DialogActions, DialogContent, DialogTitle} from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import MenuItem from '@mui/material/MenuItem';
import ButtonCreate from '../../../components/UI/button/ButtonCreate';
import ButtonExit from '../../../components/UI/button/ButtonExit';
import ButtonAdd from '../../../components/UI/button/ButtonAdd';
import TextFieldValidation from '../../../components/UI/table/customer-detales/TextFieldValidation';
import MaterialPageItemModule from '../../../components/UI/modul/MaterialPageItemModule';
import CompositionReplacementService from '../../../API/organization/CompositionReplacementService';
import {typesMaterial} from '../../../data/TypeMaterial';
import {EditLocalStoragePagesData} from '../../../utils/EditLocalStoragePagesData';
import {useLocation} from 'react-router-dom';
import {calcFilterData} from '../../../utils/common/calcFilterData';
import {CompositionReplacementsFilterData} from '../../../data/FilterTables/CompositionReplacementsFilterData';
import OrderUtil from '../../../utils/orders/OrderUtil';
import {
    COMPOSITION_MODULES_TAB_TABLE_WIDTH,
    compositionModulesTabTableScrollSx,
} from './compositionModulesTabTableLayout';

const initValue = {
    tempName: '',
    materialName: '',
    materialId: null,
    typeMaterial: 'SLAB',
};

const CompositionReplacementsTable = ({compositionId, handleModalAlert}) => {
    const pathname = useLocation().pathname;
    const [data, setData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [globalFilter, setGlobalFilter] = useState('');
    const [columnsFilter, setColumnsFilter] = useState([]);
    const [showColumnFilters, setShowColumnFilters] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [materialModalOpen, setMaterialModalOpen] = useState(false);
    const [currentEditRowData, setCurrentEditRowData] = useState(initValue);
    const [tableOptions, setTableOptions] = useState({
        pageIndex: 0,
        pageSize: 100,
        totalElements: 0,
        searchData: '',
    });

    const tempNameList = useMemo(() => OrderUtil.getListField(data, 'tempName').filter(Boolean), [data]);
    const materialNameList = useMemo(() => OrderUtil.getListField(data, 'materialName').filter(Boolean), [data]);
    const typeMaterialList = useMemo(
        () => OrderUtil.getListFieldByEnum(data, 'typeMaterial', typesMaterial),
        [data],
    );

    const tableOptionsHandle = (nextState) => {
        const resolved = typeof nextState === 'function' ? nextState(tableOptions) : nextState;
        setTableOptions(resolved);
        if (resolved.pageSize !== tableOptions.pageSize) {
            EditLocalStoragePagesData(`${pathname}/replacements`, 'pageSize', resolved.pageSize);
        }
    };

    const handleSearchQuery = (searchQuery, prevData, pageSize, isColumnFilters) => {
        const newColumnsFilter = isColumnFilters ? searchQuery : columnsFilter;
        const newGlobalFilter = isColumnFilters ? globalFilter : searchQuery || '';
        const rows = [...prevData];
        const globalFilterData = newGlobalFilter
            ? calcFilterData(rows, newGlobalFilter, CompositionReplacementsFilterData)
            : rows;
        const newData = newColumnsFilter.reduce((accumulator, currentValue) => {
            return calcFilterData(
                accumulator,
                currentValue?.value,
                [CompositionReplacementsFilterData.find((el) => el.idFromColumnFilter === currentValue.id)],
            );
        }, globalFilterData);

        setFilteredData(newData);
        tableOptionsHandle({
            ...tableOptions,
            totalElements: newData.length,
            pageSize: pageSize ? pageSize : tableOptions?.pageSize,
        });

        if (isColumnFilters) {
            setColumnsFilter(searchQuery);
        } else {
            setGlobalFilter(searchQuery || '');
            if (searchQuery !== globalFilter) {
                EditLocalStoragePagesData(`${pathname}/replacements`, 'searchQuery', searchQuery);
            }
        }
    };

    const handleShowColumnFilters = () => {
        setShowColumnFilters(!showColumnFilters);
        if (showColumnFilters) {
            handleSearchQuery([], data, null, true);
        }
    };

    useEffect(() => {
        if (!compositionId) {
            return;
        }
        const pagesData = JSON.parse(localStorage.getItem('pagesData')) || {};
        const pageSize = pagesData[`${pathname}/replacements`]?.pageSize;
        const searchQuery = pagesData[`${pathname}/replacements`]?.searchQuery;

        CompositionReplacementService.getAll(compositionId)
            .then((response) => {
                setData(response.data);
                handleSearchQuery(searchQuery, response.data, pageSize);
            })
            .catch((error) => {
                handleModalAlert(error);
            });
    }, [compositionId]);

    const handleClose = () => {
        setModalOpen(false);
        setCurrentEditRowData(initValue);
    };

    const handleEdit = (values) => {
        CompositionReplacementService.update(currentEditRowData.id, values)
            .then((response) => {
                const newData = data.map((el) => (el.id === response.data.id ? response.data : el));
                setData(newData);
                handleSearchQuery(globalFilter, newData);
                handleClose();
                handleModalAlert('Данные изменены', 'success');
            })
            .catch((error) => {
                handleModalAlert(error);
            });
    };

    const handleSubmit = (values) => {
        CompositionReplacementService.create(compositionId, values)
            .then((response) => {
                const newData = [...data, response.data];
                setData(newData);
                handleSearchQuery(globalFilter, newData);
                handleClose();
                handleModalAlert('Данные добавлены', 'success');
            })
            .catch((error) => {
                handleModalAlert(error);
            });
    };

    const handleDeleteRow = useCallback((row) => {
        CompositionReplacementService.delete(row.id)
            .then(() => {
                const newData = data.filter((el) => el.id !== row.id);
                setData(newData);
                handleSearchQuery(globalFilter, newData);
                handleModalAlert('Данные удалены', 'success');
            })
            .catch((error) => {
                handleModalAlert(error);
            });
    }, [data, globalFilter, handleModalAlert]);

    const handleUpdateRow = (row) => {
        setCurrentEditRowData(row);
        setModalOpen(true);
    };

    const columns = useMemo(() => [
        {
            id: 'tempName',
            header: 'Заменяемое поле',
            accessorKey: 'tempName',
            grow: true,
            filterVariant: 'multi-select',
            filterSelectOptions: tempNameList,
            muiFilterTextFieldProps: {
                placeholder: 'Отфильтровать по заменяемому полю',
                sx: {width: '280px'},
            },
        },
        {
            id: 'typeMaterial',
            header: 'Тип материала',
            accessorFn: (row) => typesMaterial[row.typeMaterial] || row.typeMaterial || '',
            size: 180,
            grow: false,
            filterVariant: 'multi-select',
            filterSelectOptions: typeMaterialList,
            muiFilterTextFieldProps: {
                placeholder: 'Отфильтровать по типу',
                sx: {width: '200px'},
            },
        },
        {
            id: 'materialName',
            header: 'Замена',
            accessorKey: 'materialName',
            grow: true,
            filterVariant: 'multi-select',
            filterSelectOptions: materialNameList,
            muiFilterTextFieldProps: {
                placeholder: 'Отфильтровать по замене',
                sx: {width: '280px'},
            },
        },
        {
            header: 'id',
            id: 'id',
            accessorKey: 'id',
            enableHiding: false,
            enableColumnActions: false,
            enableGrouping: false,
            enableEditing: false,
        },
    ], [tempNameList, typeMaterialList, materialNameList]);

    const menuItem = [
        {type: 'edit', text: 'Редактировать', handler: handleUpdateRow},
        {type: 'delete', text: 'Удалить', handler: handleDeleteRow},
    ];

    return (
        <Box sx={{...compositionModulesTabTableScrollSx, paddingTop: '40px'}}>
            <Box sx={{
                width: COMPOSITION_MODULES_TAB_TABLE_WIDTH,
                minWidth: COMPOSITION_MODULES_TAB_TABLE_WIDTH,
            }}>
            <Box sx={{display: 'flex', justifyContent: 'space-between', paddingBottom: '30px'}}>
                <Typography sx={{fontSize: '24px', fontWeight: 700}}>
                    Соответствие материалов
                </Typography>
                <ButtonCreate
                    text={'Создать'}
                    width={120}
                    onClick={() => {
                        setCurrentEditRowData(initValue);
                        setModalOpen(true);
                    }}
                />
            </Box>
            <MaterialReactTable
                renderTopToolbar={({table}) => (
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        padding: '7px',
                        width: COMPOSITION_MODULES_TAB_TABLE_WIDTH,
                        boxSizing: 'border-box',
                        backgroundColor: (theme) => table.getSelectedRowModel().rows.length > 0
                            ? theme.palette.primary.light
                            : 'transparent',
                    }}>
                        <MRT_ToolbarAlertBanner table={table}/>
                        <Box sx={{display: 'flex'}}>
                            <MRT_ToggleFiltersButton table={table}/>
                            <MRT_GlobalFilterTextField table={table}/>
                        </Box>
                    </Box>
                )}
                muiTablePaperProps={{
                    sx: {
                        width: 'fit-content',
                        maxWidth: 'fit-content',
                    },
                }}
                muiTableContainerProps={{
                    sx: {
                        width: COMPOSITION_MODULES_TAB_TABLE_WIDTH,
                        maxWidth: COMPOSITION_MODULES_TAB_TABLE_WIDTH,
                        maxHeight: 'calc(100vh - 420px)',
                        overflowX: 'visible',
                    },
                }}
                muiTableProps={{
                    sx: {
                        width: COMPOSITION_MODULES_TAB_TABLE_WIDTH,
                        tableLayout: 'fixed',
                    },
                }}
                muiTableBodyCellProps={{
                    sx: {
                        p: '0px 0px 0px 10px',
                        m: 0,
                        border: 'none',
                        '&:after': {display: 'none'},
                        '&:not(:last-of-type)': {borderRight: '1px solid #EAEBEA'},
                        '&:first-of-type': {
                            padding: 0,
                            width: '50px',
                            height: '36px',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            color: (theme) => theme.palette.text.light,
                            '& .MuiButtonBase-root': {
                                borderRadius: '4px',
                                width: '24px',
                                height: '24px',
                                opacity: 1,
                                margin: 0,
                                padding: 0,
                                '&:hover': {
                                    backgroundColor: (theme) => theme.palette.primary.stroke,
                                },
                            },
                        },
                    },
                }}
                muiTableHeadCellProps={{
                    sx: {
                        border: '1px solid #EAEBEA',
                        backgroundColor: '#F5F6FA',
                        padding: '10px',
                        paddingBottom: '0.2rem',
                        paddingTop: '0.2rem',
                    },
                }}
                muiTableBodyRowProps={() => ({
                    sx: () => ({
                        height: '36px',
                        backgroundColor: 'white',
                        '&:hover& td:after': {display: 'none'},
                        '&.MuiTableRow-root.Mui-selected': {
                            backgroundColor: (theme) => theme.palette.primary.light70,
                            '&:hover': {
                                backgroundColor: (theme) => theme.palette.primary.light50,
                            },
                        },
                        '&:hover': {
                            '& td:after': {display: 'none'},
                            backgroundColor: (theme) => theme.palette.primary.stroke,
                        },
                        '&:nth-of-type(even)': {
                            '&.MuiTableRow-root.Mui-selected': {
                                backgroundColor: (theme) => theme.palette.primary.light,
                                '&:hover': {
                                    backgroundColor: (theme) => theme.palette.primary.light50,
                                },
                            },
                            backgroundColor: (theme) => theme.palette.primary.lightGrey,
                            '&:hover': {
                                backgroundColor: (theme) => theme.palette.primary.stroke,
                            },
                        },
                    }),
                })}
                displayColumnDefOptions={{
                    'mrt-row-actions': {
                        header: '',
                        size: 3,
                        minSize: 3,
                        grow: false,
                    },
                }}
                muiSearchTextFieldProps={{
                    placeholder: 'Поиск',
                    sx: {
                        paddingTop: '5px',
                        paddingBottom: '0px',
                        '& .MuiCollapse-root': {
                            borderRadius: '12px',
                            height: 40,
                            width: 290,
                            '& .MuiSvgIcon-root': {color: '#737478'},
                            '& .MuiOutlinedInput-input': {padding: 0},
                        },
                    },
                    variant: 'outlined',
                }}
                columns={columns}
                data={filteredData}
                initialState={{
                    columnVisibility: {id: false},
                    density: 'compact',
                }}
                getRowId={(originalRow) => originalRow.id}
                renderRowActionMenuItems={({closeMenu, row}) => menuItem.map((el) => (
                    <MenuItem
                        key={el.type}
                        onClick={() => {
                            el.handler(row.original);
                            closeMenu();
                        }}
                        sx={{
                            margin: '0 8px',
                            borderRadius: '4px',
                            '&:hover': {backgroundColor: (theme) => theme.palette.primary.light},
                        }}
                    >
                        {el.text}
                    </MenuItem>
                ))}
                onPaginationChange={(updater) => {
                    tableOptionsHandle(typeof updater === 'function' ? updater(tableOptions) : updater);
                }}
                onGlobalFilterChange={(value) => {
                    handleSearchQuery(value, data, null);
                }}
                onColumnFiltersChange={(updater) => {
                    handleSearchQuery(updater(columnsFilter), data, null, true);
                }}
                onShowColumnFiltersChange={handleShowColumnFilters}
                manualFiltering={true}
                state={{
                    showColumnFilters,
                    columnFilters: columnsFilter,
                    globalFilter,
                    showGlobalFilter: true,
                    pagination: tableOptions,
                }}
                rowCount={tableOptions.totalElements}
                enableRowActions
                enableToolbarInternalActions={false}
                localization={MRT_Localization_RU}
            />
            </Box>
            <CreateEditReplacementModal
                title={currentEditRowData?.id ? 'Изменить' : 'Добавить'}
                open={modalOpen}
                onClose={handleClose}
                handleSubmit={currentEditRowData?.id ? handleEdit : handleSubmit}
                initValue={currentEditRowData}
                materialModalOpen={materialModalOpen}
                setMaterialModalOpen={setMaterialModalOpen}
                handleModalAlert={handleModalAlert}
            />
        </Box>
    );
};

const CreateEditReplacementModal = ({
    open,
    onClose,
    handleSubmit,
    initValue,
    title,
    materialModalOpen,
    setMaterialModalOpen,
    handleModalAlert,
}) => {
    const [formState, setFormState] = useState(initValue);
    const [validationState, setValidationState] = useState({
        tempName: '',
        typeMaterial: '',
        materialId: '',
    });

    useEffect(() => {
        setFormState(initValue);
    }, [initValue]);

    const handleClose = () => {
        setValidationState({tempName: '', typeMaterial: '', materialId: ''});
        onClose();
    };

    const handleValidationData = () => {
        const newErrors = {
            tempName: formState.tempName ? '' : 'пустое поле',
            typeMaterial: formState.typeMaterial ? '' : 'пустое поле',
            materialId: formState.materialId ? '' : 'выберите материал',
        };
        setValidationState(newErrors);

        if (Object.values(newErrors).every((el) => el === '')) {
            handleSubmit({
                tempName: formState.tempName,
                materialName: formState.materialName,
                materialId: formState.materialId,
                typeMaterial: formState.typeMaterial,
            });
        }
    };

    return (
        <>
            <Dialog
                open={open}
                PaperProps={{
                    style: {
                        minWidth: '400px',
                        borderRadius: 8,
                    },
                }}
            >
                <DialogTitle textAlign="center">{title}</DialogTitle>
                <DialogContent>
                    <TextFieldValidation
                        name={'tempName'}
                        value={formState?.tempName}
                        label="Заменяемое поле"
                        setCurrValues={setFormState}
                        validationError={validationState?.tempName}
                        setValidationState={setValidationState}
                    />
                    <TextFieldValidation
                        name={'typeMaterial'}
                        value={formState?.typeMaterial}
                        label="Тип материала"
                        select
                        setCurrValues={(updater) => {
                            setFormState((prev) => {
                                const next = typeof updater === 'function' ? updater(prev) : updater;
                                if (next.typeMaterial !== prev.typeMaterial) {
                                    return {
                                        ...next,
                                        materialId: null,
                                        materialName: '',
                                    };
                                }
                                return next;
                            });
                        }}
                        validationError={validationState?.typeMaterial}
                        setValidationState={setValidationState}
                    >
                        {Object.entries(typesMaterial).map(([key, label]) => (
                            <MenuItem key={key} value={key}>{label}</MenuItem>
                        ))}
                    </TextFieldValidation>
                    <TextFieldValidation
                        name={'materialName'}
                        value={formState?.materialName || ''}
                        label="Замена"
                        disabled
                        required={false}
                        setCurrValues={setFormState}
                        validationError={validationState?.materialId}
                        setValidationState={setValidationState}
                    />
                    <Box sx={{display: 'flex', justifyContent: 'flex-end', mt: 1}}>
                        <ButtonCreate
                            text={'Выбрать материал'}
                            width={180}
                            onClick={() => setMaterialModalOpen(true)}
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{p: '1.25rem'}}>
                    <ButtonExit text={'Отменить'} onClick={handleClose}/>
                    <ButtonAdd text={'Сохранить'} onClick={handleValidationData}/>
                </DialogActions>
            </Dialog>
            <Dialog
                open={materialModalOpen}
                onClose={() => setMaterialModalOpen(false)}
                PaperProps={{style: {minWidth: '1500px'}}}
            >
                <DialogTitle textAlign="center">Выбрать материал</DialogTitle>
                <DialogContent>
                    {materialModalOpen && (
                        <MaterialPageItemModule
                            key={formState.typeMaterial}
                            isOrganization={false}
                            selectMaterialId={formState.materialId}
                            selectMaterial={(material) => setFormState((prev) => ({
                                ...prev,
                                materialId: material.id,
                                materialName: material.name,
                            }))}
                            typeMaterial={formState.typeMaterial}
                            modal
                            handleModalAlert={handleModalAlert}
                        />
                    )}
                </DialogContent>
                <DialogActions sx={{p: '1.25rem'}}>
                    <ButtonExit text={'Выйти'} onClick={() => setMaterialModalOpen(false)}/>
                    <ButtonAdd
                        text={'Выбрать'}
                        disabled={!formState.materialId}
                        onClick={() => setMaterialModalOpen(false)}
                    />
                </DialogActions>
            </Dialog>
        </>
    );
};

export default CompositionReplacementsTable;
